import { libraryApi, mappingApi } from "@iventis/api/src/api";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Node } from "@iventis/domain-model/model/node";
import { findNodesByType } from "@iventis/tree-browser";
import qs from "qs";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { ViewByStatus } from "@iventis/map-engine/src/types/layer-view-by";
import { getMapLayers as getMapLayersFromMappingApi } from "@iventis/api-helpers-mapping";

/**
 * Given a layer id, will get the map it belongs to and then the full map node with all children
 */
export async function getMapNodeFromLayerId(layerId: string) {
    // Get the map node that is belonging to the layer
    const { data: layersParentNodes } = await libraryApi.get<Node>(`/nodes/${layerId}/parents-and-children`, {
        params: {
            expandNodeType: [NodeType.Map, NodeType.Folder, NodeType.Layer],
            sort: "nameFolderFirstAsc",
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    });

    const layerMapNodes = findNodesByType([layersParentNodes], NodeType.Map);

    if (layerMapNodes.length < 1) {
        throw new Error("Could not find map node");
    }

    if (layerMapNodes.length > 1) {
        throw new Error("Found more than one map node");
    }

    const layerMapNode = layerMapNodes[0];

    // Get all the children for the map node
    const { data: mapWithAllChildren } = await libraryApi.get<Node>(`nodes/${layerMapNode.id}/children`, {
        params: {
            expandNodeType: [NodeType.PersonalMappingLibrary, NodeType.MappingLibrary, NodeType.Folder, NodeType.Map],
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    });

    return mapWithAllChildren;
}

/** Fetch all of the layers belonging to a map and get the properties needed for layer ordering */
export async function getMapLayers(mapId: string): Promise<Pick<DomainLayer, "id" | "sidebarOrder" | "viewBy">[]> {
    const layers = await getMapLayersFromMappingApi(mapId, mappingApi);

    const domainLayers: Pick<DomainLayer, "id" | "sidebarOrder" | "viewBy">[] = layers.map((l) => ({
        id: l.id,
        sidebarOrder: l.sidebarOrder,
        viewBy: ViewByStatus.OBJECT,
    }));
    return domainLayers;
}
